import { MoneySchema } from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { MercuryModelMode } from "../conversion/mercury-model";
import { CorrelatedCarrierServiceSchema } from "../fulfillments/fulfillment-carriers-and-services";
import {
  DynamicRateRounding,
  RateTableType,
} from "../integration/redo-in-shipping/shipping-rate-table";
import { getParcelsForCheckoutFromOmsAutomationResultSchema } from "./checkout-oms-automation-utils";

const landedCostDataSchema_ = z.object({
  zonosQuoteId: z.string(),
  duties: z.number(), // minor units
  taxes: z.number(), // minor units
  fees: z.number(), // minor units
  currency: createEnum(Currency, { enumName: "Currency" }),
});
export const landedCostDataSchema: z.ZodType<LandedCostData> =
  landedCostDataSchema_;
export interface LandedCostData extends z.infer<typeof landedCostDataSchema_> {}

const baseRedoShippingOptionSchema = z.object({
  provider: z.literal("redo"),
  rateTableId: zExt.objectId(),
  checkoutTreeNodeId: z.string(),
  checkoutExperienceId: z.string().optional(),
  assignedTreatmentId: z.string().optional(),
  coverageTypes: z
    .object({
      return: z.boolean().optional(),
      packageProtection: z.boolean().optional(),
    })
    .optional(),
  landedCostData: landedCostDataSchema.optional(),
});

const markupSchema = z.object({
  markupPercentage: z.string().optional(),
  markupAmount: z.string().optional(),
});

const costBreakdownSchema = z.object({
  costCurrency: createEnum(Currency, { enumName: "Currency" }),
  labelCost: z.string(),
  markup: markupSchema.optional(),
  rounding: createEnum(DynamicRateRounding, {
    enumName: "DynamicRateRounding",
  }).optional(),
});
export type CostBreakdown = z.infer<typeof costBreakdownSchema>;

const dynamicRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.DYNAMIC),
  carrierService: CorrelatedCarrierServiceSchema,
  /**
   * Multiple parcels can cause a rate table to have multiple carrier services
   */
  carrierServices: z.array(CorrelatedCarrierServiceSchema).optional(),
  costBreakdown: costBreakdownSchema,
  promisedDeliveryDate: z.string().optional(),
});

const fixedRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.FIXED),
});

export const checkoutShippingOptionSchema = z.union([
  dynamicRedoShippingOptionSchema,
  fixedRedoShippingOptionSchema,
]);

export type CheckoutShippingOption = z.infer<
  typeof checkoutShippingOptionSchema
>;

export const featureValuePairSchema = z.array(
  z.tuple([z.string(), z.number()]),
);
export const optionWeightsSchema = z.record(z.string(), featureValuePairSchema);

const baseMercuryResultSchema = z.object({
  checkoutExperienceId: z.string(),
  checkoutTreeNodeId: z.string(),
  optimalPrices: z.record(z.string(), z.number()),
  labelCostShopMoneyByRateTableId: z.record(z.string(), MoneySchema).nullish(),
});

export const mercuryLiteResultSchema = baseMercuryResultSchema.extend({
  modelMode: z.literal(MercuryModelMode.LITE_ENABLED),
});
export type MercuryLiteResult = z.infer<typeof mercuryLiteResultSchema>;

const mercuryFullResultSchema = baseMercuryResultSchema.extend({
  modelMode: z.union([
    z.literal(MercuryModelMode.ENABLED),
    z.literal(MercuryModelMode.SHADOW),
  ]),
  expectedMargin: z.number(),
  expectedMarginsByRateTable: z.record(z.string(), z.number()),
  priceCombinationsExplored: z.number().nullish(),
  betaWeightsMean: optionWeightsSchema.nullish(),
  betaWeightsStd: optionWeightsSchema.nullish(),
  betaWeightsSampled: optionWeightsSchema.nullish(),
  featuresWithOptimalPrices: featureValuePairSchema.nullish(),
  inferenceTimeMs: z.number(),
});

export const mercuryResultSchema = z.union([
  mercuryLiteResultSchema,
  mercuryFullResultSchema,
]);
export type MercuryResult = z.infer<typeof mercuryResultSchema>;

export enum SelectedParcelSource {
  OMS_AUTOMATION = "OMS_AUTOMATION",
  PARCEL_TREE = "PARCEL_TREE",
  DEFAULT = "DEFAULT",
}

const parcelSelectionDecisionInfoSchema = z.union([
  z.object({
    source: z.literal(SelectedParcelSource.OMS_AUTOMATION),
    omsAutomationParcelResult:
      getParcelsForCheckoutFromOmsAutomationResultSchema,
  }),
  z.object({ source: z.literal(SelectedParcelSource.PARCEL_TREE) }),
  z.object({ source: z.literal(SelectedParcelSource.DEFAULT) }),
]);

const selectedParcelInfoSchema = z.object({
  selectedParcelIds: z.array(z.string()),
  parcelSelectionDecisionInfo: parcelSelectionDecisionInfoSchema,
});
export type SelectedParcelInfo = z.infer<typeof selectedParcelInfoSchema>;

export const checkoutShippingOptionsDocumentSchema = z.object({
  _id: zExt.objectId(),
  stableId: z.string().optional(),
  team: zExt.objectId(),
  options: z.array(checkoutShippingOptionSchema),
  checkoutExperienceIdToMercuryModelId: z
    .record(z.string(), z.string().nullish())
    .nullish(),
  mercuryResults: z.array(mercuryResultSchema).nullish(),
  selectedParcelInfoByRateTableId: z
    .record(z.string(), selectedParcelInfoSchema)
    .nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type CheckoutShippingOptionsDocument = z.infer<
  typeof checkoutShippingOptionsDocumentSchema
>;

/**
 * Input schema for creating a new document (without timestamps and _id)
 */
export const createCheckoutShippingOptionsDocumentSchema =
  checkoutShippingOptionsDocumentSchema.omit({
    _id: true,
    stableId: true,
    createdAt: true,
    updatedAt: true,
  });

export type CreateCheckoutShippingOptionsDocument = z.infer<
  typeof createCheckoutShippingOptionsDocumentSchema
>;
